/**
 * The todos area's writes, made offline (unit 25, rule 11: the first synced
 * area). Each one shows at once, from the cache, and queues the request that
 * makes it true on the server — a create by id, a state, or a delete.
 *
 * The cached row a write leaves is a guess at the server's answer, and only
 * until the next change replaces it: `sort_order`, for one, is the server's to
 * decide (todos rule 5), so a new todo sits last until then.
 *
 * **Day-bound fields are bounds, not the rule** (27A, Q2 (a)). A todo made due
 * today features its list at once — the server's answer would say so too — but
 * nothing here un-features one: that waits for the server.
 */

import type { Schemas } from "../api/client";
import { edit } from "./cache";
import { enqueue } from "./outbox";
import { uuid7 } from "./uuid7";

/** A title, and anything else the server would otherwise default. The id is made here. */
type TodoIn = Pick<Schemas["TodoIn"], "title"> & Partial<Omit<Schemas["TodoIn"], "id" | "title">>;
type TodoOut = Schemas["TodoOut"];
type ListOut = Schemas["ListOut"];
type HabitOut = Schemas["HabitOut"];
type HabitIn = Pick<Schemas["HabitIn"], "title"> &
  Partial<Omit<Schemas["HabitIn"], "id" | "title">>;

export function localDay(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export async function createTodo(
  db: IDBDatabase,
  input: TodoIn,
  now: Date = new Date(),
): Promise<TodoOut> {
  const id = uuid7(now.getTime());
  const stamp = now.toISOString();
  const row: TodoOut = {
    id,
    title: input.title,
    list_id: input.list_id ?? null,
    notes: input.notes ?? null,
    due_date: input.due_date ?? null,
    completed_at: null,
    sort_order: Number.MAX_SAFE_INTEGER,
    created_by: input.created_by ?? "user",
    tags: input.tags ?? [],
    goal_ids: input.goal_ids ?? [],
    reminder: input.reminder ?? null,
    created_at: stamp,
    updated_at: stamp,
    overdue: input.due_date != null && input.due_date < localDay(now),
  };
  await enqueue(
    db,
    {
      method: "POST",
      path: "/api/todos/items",
      body: { ...input, id },
      entity: "todo",
      entityId: id,
    },
    {
      now,
      effect: (cache) => {
        cache.put({ entity: "todo", id, row });
        feature(cache, row, now);
      },
    },
  );
  return row;
}

/** The bound: a todo due today features its list (rule 10's "due today" half). */
function feature(cache: IDBObjectStore, todo: Pick<TodoOut, "list_id" | "due_date">, now: Date) {
  if (todo.list_id && todo.due_date === localDay(now))
    edit(cache, "list", todo.list_id, (row) => ({ ...row, featured: true }));
}

export function updateTodo(
  db: IDBDatabase,
  id: string,
  patch: Schemas["TodoPatch"],
  now: Date = new Date(),
): Promise<void> {
  const shown = Object.fromEntries(
    Object.entries(patch).map(([key, value]) => [key, value === "clear" ? null : value]),
  );
  return enqueue(
    db,
    { method: "PATCH", path: `/api/todos/items/${id}`, body: patch, entity: "todo", entityId: id },
    {
      now,
      effect: (cache) =>
        edit(cache, "todo", id, (row) => {
          const next = { ...row, ...shown, id } as TodoOut;
          feature(cache, next, now);
          return next;
        }),
    },
  );
}

export function setTodoCompleted(
  db: IDBDatabase,
  id: string,
  completed: boolean,
  now: Date = new Date(),
): Promise<void> {
  return enqueue(
    db,
    {
      method: "PUT",
      path: `/api/todos/items/${id}/completion`,
      body: { completed },
      entity: "todo",
      entityId: id,
    },
    {
      now,
      effect: (cache) =>
        edit(cache, "todo", id, (row) => ({
          ...row,
          completed_at: completed ? now.toISOString() : null,
          overdue: completed ? false : row.due_date != null && row.due_date < localDay(now),
        })),
    },
  );
}

export function deleteTodo(db: IDBDatabase, id: string, now: Date = new Date()): Promise<void> {
  return enqueue(
    db,
    { method: "DELETE", path: `/api/todos/items/${id}`, entity: "todo", entityId: id },
    { now, effect: (cache) => cache.delete(["todo", id]) },
  );
}

/**
 * For the day it was made — the server reads that from `X-Made-At` (rule 3) —
 * or for `on`, a day the person picked (the heat map, a past day on Today).
 * The cached row holds today's state, so only a write for today changes it.
 */
export function setHabitCompletion(
  db: IDBDatabase,
  id: string,
  completed: boolean,
  value: number | null = null,
  now: Date = new Date(),
  on?: string,
): Promise<void> {
  const today = on === undefined || on === localDay(now);
  return enqueue(
    db,
    {
      method: "PUT",
      path: `/api/todos/habits/${id}/completion${today ? "" : `?on=${on}`}`,
      body: { completed, value },
      entity: "habit",
      entityId: id,
    },
    {
      now,
      effect: (cache) => {
        if (today)
          edit(cache, "habit", id, (row) => ({
            ...row,
            completed,
            completion_value: completed ? value : null,
            // A bound until the server's row returns (rule 15).
            done: row.type === "negative" ? row.done : completed,
          }));
      },
    },
  );
}

export function setHabitSlip(
  db: IDBDatabase,
  id: string,
  slipped: boolean,
  now: Date = new Date(),
  on?: string,
): Promise<void> {
  const today = on === undefined || on === localDay(now);
  return enqueue(
    db,
    {
      method: "PUT",
      path: `/api/todos/habits/${id}/slip${today ? "" : `?on=${on}`}`,
      body: { slipped },
      entity: "habit",
      entityId: id,
    },
    {
      now,
      effect: (cache) => {
        if (today)
          edit(cache, "habit", id, (row) => ({
            ...row,
            slipped,
            done: row.type === "negative" ? !slipped : row.done,
          }));
      },
    },
  );
}

/** One order across todos and habits (rule 4): each item's new `sort_order`. */
export function reorder(
  db: IDBDatabase,
  items: Schemas["ReorderItem"][],
  now: Date = new Date(),
): Promise<void> {
  const [first] = items;
  if (!first) return Promise.resolve();
  return enqueue(
    db,
    // The outbox keys a write by one entity; a reorder names its first item.
    {
      method: "POST",
      path: "/api/todos/reorder",
      body: items,
      entity: kindOf(first),
      entityId: first.id,
    },
    {
      now,
      effect: (cache) => {
        for (const item of items)
          edit(cache, kindOf(item), item.id, (row) => ({ ...row, sort_order: item.sort_order }));
      },
    },
  );
}

function kindOf(item: Schemas["ReorderItem"]): "todo" | "habit" {
  return item.kind === "habit" ? "habit" : "todo";
}

// ── Lists ─────────────────────────────────────────────────────────────────────

export async function createList(
  db: IDBDatabase,
  input: Pick<Schemas["TodoListIn"], "name"> & Partial<Omit<Schemas["TodoListIn"], "id" | "name">>,
  now: Date = new Date(),
): Promise<ListOut> {
  const id = uuid7(now.getTime());
  const stamp = now.toISOString();
  const row: ListOut = {
    id,
    name: input.name,
    color: input.color ?? null,
    type: input.type ?? null,
    pinned: input.pinned ?? false,
    sort_order: Number.MAX_SAFE_INTEGER,
    created_by: "user",
    created_at: stamp,
    updated_at: stamp,
    featured: input.pinned ?? false,
  };
  await enqueue(
    db,
    {
      method: "POST",
      path: "/api/todos/lists",
      body: { ...input, id },
      entity: "list",
      entityId: id,
    },
    { now, effect: (cache) => cache.put({ entity: "list", id, row }) },
  );
  return row;
}

export function updateList(
  db: IDBDatabase,
  id: string,
  patch: Schemas["ListPatch"],
  now: Date = new Date(),
): Promise<void> {
  return enqueue(
    db,
    { method: "PATCH", path: `/api/todos/lists/${id}`, body: patch, entity: "list", entityId: id },
    {
      now,
      effect: (cache) =>
        edit(cache, "list", id, (row) => ({
          ...row,
          ...Object.fromEntries(Object.entries(patch).filter(([, v]) => v != null)),
          // Pinning features at once; un-pinning waits for the server (a todo may be due).
          featured: row.featured || patch.pinned === true,
        })),
    },
  );
}

/** Its todos and habits stay, with no list — as the server does it (foreign keys). */
export function deleteList(db: IDBDatabase, id: string, now: Date = new Date()): Promise<void> {
  return enqueue(
    db,
    { method: "DELETE", path: `/api/todos/lists/${id}`, entity: "list", entityId: id },
    {
      now,
      effect: (cache) => {
        cache.delete(["list", id]);
        const walk = cache.openCursor();
        walk.onsuccess = () => {
          const at = walk.result;
          if (!at) return;
          const value = at.value as { entity: string; row: { list_id?: string | null } };
          if ((value.entity === "todo" || value.entity === "habit") && value.row.list_id === id)
            at.update({ ...value, row: { ...value.row, list_id: null } });
          at.continue();
        };
      },
    },
  );
}

// ── Habits ────────────────────────────────────────────────────────────────────

export async function createHabit(
  db: IDBDatabase,
  input: HabitIn,
  now: Date = new Date(),
): Promise<HabitOut> {
  const id = uuid7(now.getTime());
  const stamp = now.toISOString();
  const row: HabitOut = {
    id,
    title: input.title,
    list_id: input.list_id ?? null,
    notes: input.notes ?? null,
    frequency: input.frequency ?? "daily",
    days_of_week: input.days_of_week ?? null,
    day_of_month: input.day_of_month ?? null,
    target_value: input.target_value ?? null,
    unit: input.unit ?? null,
    color: input.color ?? null,
    reminder_time: input.reminder_time ?? null,
    type: input.type ?? "positive",
    archived: false,
    sort_order: Number.MAX_SAFE_INTEGER,
    created_by: "user",
    created_at: stamp,
    updated_at: stamp,
    completed: false,
    completion_value: null,
    slipped: false,
    done: input.type === "negative",
  };
  await enqueue(
    db,
    {
      method: "POST",
      path: "/api/todos/habits",
      body: { ...input, id },
      entity: "habit",
      entityId: id,
    },
    { now, effect: (cache) => cache.put({ entity: "habit", id, row }) },
  );
  return row;
}

export function updateHabit(
  db: IDBDatabase,
  id: string,
  patch: Schemas["HabitPatch"],
  now: Date = new Date(),
): Promise<void> {
  const shown = Object.fromEntries(
    Object.entries(patch)
      .filter(([, v]) => v != null)
      .map(([key, value]) => [key, value === "clear" ? null : value]),
  );
  return enqueue(
    db,
    {
      method: "PATCH",
      path: `/api/todos/habits/${id}`,
      body: patch,
      entity: "habit",
      entityId: id,
    },
    { now, effect: (cache) => edit(cache, "habit", id, (row) => ({ ...row, ...shown, id })) },
  );
}

export function deleteHabit(db: IDBDatabase, id: string, now: Date = new Date()): Promise<void> {
  return enqueue(
    db,
    { method: "DELETE", path: `/api/todos/habits/${id}`, entity: "habit", entityId: id },
    { now, effect: (cache) => cache.delete(["habit", id]) },
  );
}
